// src/auth/useAuthState.js
import { useReducer, useEffect, useRef } from 'react';
import { API_V1, getSessionHeaders } from '../services/api/config.js';

// ── localStorage helpers ─────────────────────────────────────────────────
export function saveAuth({ token, tenants }) {
  localStorage.setItem('auth_token', token);
  localStorage.setItem('auth_tenants', JSON.stringify(tenants));
}

export function saveTenant(tenantId) {
  localStorage.setItem('auth_tenant_id', tenantId);
}

export function saveTenants(tenants) {
  localStorage.setItem('auth_tenants', JSON.stringify(tenants));
}

export function clearAuthStorage() {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_tenants');
  localStorage.removeItem('auth_tenant_id');
  localStorage.removeItem('auth_admin_mode');
}

export function loadAuthStorage() {
  const token = localStorage.getItem('auth_token');
  const tenants = JSON.parse(localStorage.getItem('auth_tenants') || 'null');
  const tenantId = localStorage.getItem('auth_tenant_id');
  return { token, tenants, tenantId };
}

// ── Reducer ──────────────────────────────────────────────────────────────
const initialState = {
  phase: 'booting', // 'booting' | 'login' | 'no_tenants' | 'tenant_pick' | 'ready'
  user: null,       // { user_id, email, is_active }
  token: null,
  tenants: [],      // [{ tenant_id, role, display_name }]
  activeTenant: null,
  applications: [], // [{ id, display_name, description, sort_order }]
  isSuperadmin: false,
  adminMode: false,
  error: null,
};

export function authReducer(state, action) {
  switch (action.type) {
    case 'BOOT_DONE': {
      const adminMode = !!action.adminMode;
      localStorage.setItem('auth_admin_mode', adminMode ? '1' : '');
      return {
        ...state,
        phase: action.phase,
        user: action.user,
        token: action.token,
        tenants: action.tenants,
        activeTenant: action.activeTenant,
        applications: action.applications,
        isSuperadmin: action.isSuperadmin ?? false,
        adminMode,
      };
    }
    case 'SET_SUPERADMIN':
      return { ...state, isSuperadmin: action.value };
    case 'SET_ADMIN_MODE': {
      localStorage.setItem('auth_admin_mode', action.value ? '1' : '');
      // Hybrid-SA flow: entering admin mode while on the tenant picker must advance
      // phase past 'tenant_pick', otherwise AppShell bounces /admin/* back to /select-tenant.
      const phase = action.value && state.phase === 'tenant_pick' ? 'ready' : state.phase;
      return { ...state, adminMode: action.value, phase };
    }
    case 'LOGIN_SUCCESS':
      return { ...state, phase: action.phase, user: action.user, token: action.token, tenants: action.tenants, error: null };
    case 'TENANT_SELECTED':
      return { ...state, phase: 'ready', activeTenant: action.activeTenant, applications: action.applications };
    case 'LOGOUT':
      return { ...initialState, phase: 'login' };
    case 'SET_ERROR':
      return { ...state, error: action.error };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'SESSION_EXPIRED':
      return { ...initialState, phase: 'login' };
    case 'TENANT_DENIED':
      return { ...state, activeTenant: null, phase: 'tenant_pick' };
    case 'TENANTS_REFRESHED': {
      const newTenants = action.tenants || [];
      if (!state.activeTenant) {
        return { ...state, tenants: newTenants };
      }
      const stillThere = newTenants.find(t => t.tenant_id === state.activeTenant.tenant_id);
      if (stillThere) {
        return { ...state, tenants: newTenants, activeTenant: stillThere };
      }
      // Active tenant was removed — navigation side-effect handled in AuthContext
      return { ...state, tenants: newTenants, activeTenant: null };
    }
    default:
      return state;
  }
}

// ── Boot sequence ────────────────────────────────────────────────────────
async function bootSequence(dispatch) {
  const { token, tenants, tenantId } = loadAuthStorage();

  if (!token) {
    dispatch({ type: 'BOOT_DONE', phase: 'login', user: null, token: null, tenants: [], activeTenant: null, applications: [], adminMode: false });
    return;
  }

  // Validate token with GET /me
  let user;
  try {
    const res = await fetch(`${API_V1}/auth/me`, {
      headers: { Authorization: `Bearer ${token}`, ...getSessionHeaders() },
    });
    if (!res.ok) throw new Error('invalid');
    user = await res.json();
  } catch {
    clearAuthStorage();
    dispatch({ type: 'BOOT_DONE', phase: 'login', user: null, token: null, tenants: [], activeTenant: null, applications: [], adminMode: false });
    return;
  }

  const resolvedTenants = user.tenants || tenants || [];
  const isSuperadmin = !!user.is_superadmin;

  if (resolvedTenants.length === 0) {
    if (isSuperadmin) {
      // Pure superadmin: empty tenants is expected. Land directly in admin mode.
      dispatch({
        type: 'BOOT_DONE',
        phase: 'ready',
        user,
        token,
        tenants: [],
        activeTenant: null,
        applications: [],
        isSuperadmin: true,
        adminMode: true,
      });
      return;
    }
    dispatch({
      type: 'BOOT_DONE',
      phase: 'no_tenants',
      user,
      token,
      tenants: [],
      activeTenant: null,
      applications: [],
      isSuperadmin,
      adminMode: false,
    });
    return;
  }

  // Restore saved tenant
  if (!tenantId) {
    dispatch({ type: 'BOOT_DONE', phase: 'tenant_pick', user, token, tenants: resolvedTenants, activeTenant: null, applications: [], isSuperadmin, adminMode: false });
    return;
  }

  const activeTenant = resolvedTenants.find(t => t.tenant_id === tenantId) || resolvedTenants[0];

  // Load applications for this tenant
  let applications = [];
  try {
    const res = await fetch(`${API_V1}/user-config/tenants/${activeTenant.tenant_id}/applications`, {
      headers: { Authorization: `Bearer ${token}`, ...getSessionHeaders() },
    });
    if (res.ok) applications = await res.json();
  } catch { /* non-fatal */ }

  const adminMode = isSuperadmin && !!localStorage.getItem('auth_admin_mode');
  dispatch({ type: 'BOOT_DONE', phase: 'ready', user, token, tenants: resolvedTenants, activeTenant, applications, isSuperadmin, adminMode });
}

// ── Hook ─────────────────────────────────────────────────────────────────
export function useAuthState() {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const booted = useRef(false);

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    bootSequence(dispatch);
  }, []);

  return { state, dispatch };
}
